import { useEffect, useState } from "react";
import { X, FileImage, Loader2 } from "lucide-react";
import type { PrescriptionItem } from "./types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";

interface PrescriptionModalProps {
  orderId: number;
  orderNumber: string;
  onClose: () => void;
}

const PrescriptionModal: React.FC<PrescriptionModalProps> = ({ orderId, orderNumber, onClose }) => {
  const [prescriptions, setPrescriptions] = useState<PrescriptionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrescriptions();
  }, [orderId]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderPrescriptions(orderId);
      if (response.success) setPrescriptions(response.data);
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const patientUploads = prescriptions.filter((p) => p.source === "PATIENT_UPLOAD");
  const adminGenerated = prescriptions.filter((p) => p.source === "ADMIN_GENERATED");
  const doctorGenerated = prescriptions.filter((p) => p.source === "DOCTOR_GENERATED");

  const renderSection = (title: string, items: PrescriptionItem[], color: "sky" | "purple" | "emerald") => {
    if (items.length === 0) return null;
    const colorMap = {
      sky: { bg: "bg-sky-50", text: "text-sky-600", badge: "bg-sky-100 text-sky-700" },
      purple: { bg: "bg-purple-50", text: "text-purple-600", badge: "bg-purple-100 text-purple-700" },
      emerald: { bg: "bg-emerald-50", text: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
    };
    const c = colorMap[color];
    return (
      <div className="mb-5">
        <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-3 ${c.text}`}>
          <FileImage size={14} />
          {title} ({items.length})
        </div>
        <div className="grid grid-cols-2 gap-3">
          {items.map((rx, i) => (
            <div key={i} className="rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
              <img
                src={rx.media}
                alt={`${title} ${i + 1}`}
                className="w-full h-44 object-cover bg-slate-100 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(rx.media, "_blank")}
              />
              <div className="px-3 py-2 flex items-center justify-between bg-slate-50 border-t border-slate-100">
                <span className="text-xs text-slate-500">{rx.note || "No note"}</span>
                <span className={`text-[0.6rem] font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>
                  {title.split(" ")[0]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-[720px] w-[95%] max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-bold text-slate-800">Prescriptions — #{orderNumber}</h3>
          <button
            className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
              <p className="mt-2 text-sm">Loading prescriptions...</p>
            </div>
          ) : prescriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FileImage size={24} />
              </div>
              <p className="text-sm font-semibold">No prescriptions found</p>
            </div>
          ) : (
            <>
              {renderSection("Patient Upload", patientUploads, "sky")}
              {renderSection("Admin Generated", adminGenerated, "purple")}
              {renderSection("Doctor Generated", doctorGenerated, "emerald")}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionModal;
